import React from 'react';

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '-');
const BLANK = '__________';

const Sections = ({ items, testid }) => (
  <div data-testid={testid}>
    {items.map(s => (
      <p key={s.num} className="text-sm leading-relaxed mb-2 whitespace-pre-line"><b>{s.num}{/^\d+$/.test(s.num) ? '.' : ''} {s.title}</b>{/^\d+$/.test(s.num) && s.title === s.title.toUpperCase() ? '\n' : ' '}{s.body}</p>
    ))}
  </div>
);

const Rows = ({ rows, testid }) => (
  <table className="w-full text-sm mb-3" data-testid={testid}><tbody>
    {rows.map(([k, v]) => <tr key={k} className="border-b border-gray-200 align-top"><td className="py-1 pr-4 text-gray-600 w-1/3">{k}</td><td className="py-1 font-semibold">{v}</td></tr>)}
  </tbody></table>
);

const Line = ({ label, value, testid }) => (
  <p className="text-sm mb-1"><span className="text-gray-600">{label}:</span> <span className="font-semibold border-b border-gray-400 inline-block min-w-[12rem]" data-testid={testid}>{value || BLANK}</span></p>
);

const SignatureBlocks = ({ terms, agreement, signatureUrl, children }) => {
  const e = terms.entity || {};
  const sub = agreement?.subscriber || {};
  const date = agreement ? new Date(agreement.signed_at).toLocaleDateString() : '';
  return (
    <section className="mt-8 pt-4 border-t border-gray-400 break-inside-avoid" data-testid="agreement-signatures">
      <h2 className="font-bold mb-4">SIGNATURES</h2>
      <div className="grid sm:grid-cols-2 gap-8">
        <div data-testid="agreement-beth-signature">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">BETH / Creditor</p>
          <Line label="Legal entity" value={e.name} />
          <Line label="Authorized signatory" value={e.signatory} />
          <Line label="Title" value={e.signatory_title} />
          <Line label="Signature" value={agreement && e.signatory ? `/s/ ${e.signatory}` : ''} testid="agreement-beth-sig-value" />
          <Line label="Date" value={agreement && e.signatory ? date : ''} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Subscriber / Borrower</p>
          {agreement ? (
            <div data-testid="agreement-signed-block">
              <Line label="Legal name" value={sub.legal_name || agreement.signer_name} testid="agreement-signed-name" />
              <Line label="Entity title, if applicable" value={sub.entity_title} />
              {signatureUrl && <img src={signatureUrl} alt="Borrower signature" className="h-16 max-w-[18rem] object-contain object-left" data-testid="agreement-signature-img" />}
              <Line label="Date" value={date} />
              <p className="text-xs text-gray-500 mt-1">Signed electronically {new Date(agreement.signed_at).toLocaleString()} &middot; ID {agreement.agreement_id.slice(0, 8).toUpperCase()} &middot; {agreement.version}</p>
            </div>
          ) : children}
        </div>
      </div>
    </section>
  );
};

export const AgreementDocument = ({ data, signatureUrl, children }) => {
  const { terms, statement } = data;
  const agreement = data.signed ? data.agreement : null;
  const h = terms.header;
  const a = terms.schedule_a;
  const note = terms.promissory_note;
  return (
    <div className="bg-white text-gray-900 p-6 sm:p-10 font-serif print:p-0" data-testid="credit-agreement-document">
      <div className="text-center border-b-2 border-gray-900 pb-4 mb-6">
        <h1 className="text-2xl font-bold tracking-wide">{h.title}</h1>
        <p className="text-sm font-bold mt-1" data-testid="agreement-title">{h.subtitle}</p>
        <p className="text-xs text-gray-500 mt-1">Version {terms.version}</p>
      </div>
      <Rows rows={h.fields} testid="agreement-header-fields" />
      {h.parties.map(([k, v]) => <p key={k} className="text-sm mb-2" data-testid={`agreement-party-${k.startsWith('Creditor') ? 'beth' : 'subscriber'}`}><b>{k}:</b> {v}</p>)}
      <p className="text-sm mb-4">{h.closing}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 text-sm font-sans print:hidden" data-testid="agreement-key-terms">
        {[['Credit limit', usd(terms.limit_usd)], ['Term', `${terms.term_years} years`], ['Interest', `${terms.apr * 100}% / year, interest only`], ['Repayment', terms.repayment_rate],
          ['Effective', fmt(terms.effective_date)], ['Maturity', fmt(terms.maturity_date)], ['First payment', fmt(terms.first_payment_due)], ['Payment now', `${usd(statement.monthly_payment_usd)} / mo`]].map(([k, v]) => (
          <div key={k} className="border border-gray-300 rounded p-2"><p className="text-[10px] uppercase text-gray-500">{k}</p><p className="font-semibold">{v}</p></div>
        ))}
      </div>
      {terms.articles.map((art, i) => (
        <section key={art.title} className="mb-4"><h2 className="font-bold text-sm mb-2">{art.title}</h2><Sections items={art.sections} testid={`agreement-article-${i + 1}`} /></section>
      ))}
      <SignatureBlocks terms={terms} agreement={agreement} signatureUrl={signatureUrl}>{children}</SignatureBlocks>
      <section className="mt-8 pt-4 border-t border-gray-400 break-before-page" data-testid="agreement-schedule-a">
        <h2 className="font-bold mb-2">{a.title}</h2>
        <Rows rows={a.rows} />
        <p className="text-xs italic mb-2">{a.note}</p>
        <div className="flex gap-8 text-sm">{a.initials.map(([k, v]) => <p key={k}>{k}: <b className="border-b border-gray-400 px-2">{v}</b></p>)}</div>
      </section>
      <section className="mt-8 pt-4 border-t border-gray-400" data-testid="agreement-schedule-b">
        <h2 className="font-bold mb-2">{terms.schedule_b.title}</h2>
        <Rows rows={terms.schedule_b.rows} />
      </section>
      <section className="mt-8 pt-4 border-t-2 border-gray-900 break-before-page" data-testid="agreement-promissory-note">
        <p className="text-xs text-center uppercase tracking-widest text-gray-500">Document 2</p>
        <h2 className="font-bold text-lg text-center">{note.title}</h2>
        <p className="font-bold text-center mb-3" data-testid="promissory-note-title">{note.subtitle}</p>
        <Rows rows={note.fields} testid="promissory-note-fields" />
        <Sections items={note.sections} testid="promissory-note-sections" />
        <NoteSignatures terms={terms} agreement={agreement} signatureUrl={signatureUrl} />
        <AdvanceRecord rows={note.advance_record || []} />
      </section>
    </div>
  );
};

const NoteSignatures = ({ terms, agreement, signatureUrl }) => {
  const e = terms.entity || {};
  const sub = agreement?.subscriber || {};
  const date = agreement ? new Date(agreement.signed_at).toLocaleDateString() : '';
  return (
    <div className="grid sm:grid-cols-2 gap-8 mt-6 break-inside-avoid" data-testid="promissory-note-signature">
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Borrower / Maker</p>
        <Line label="Full legal name" value={sub.legal_name || agreement?.signer_name} />
        <Line label="Address" value={sub.address} />
        {agreement && signatureUrl ? <img src={signatureUrl} alt="Borrower signature" className="h-14 max-w-[16rem] object-contain object-left" data-testid="promissory-note-signature-img" />
          : <Line label="Signature" value={agreement ? `/s/ ${sub.legal_name}` : ''} />}
        <Line label="Date" value={date} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Lender / Payee</p>
        <Line label="Full legal entity name" value={e.name} />
        <Line label="Authorized signatory" value={e.signatory} />
        <Line label="Title" value={e.signatory_title} />
        <Line label="Signature" value={agreement && e.signatory ? `/s/ ${e.signatory}` : ''} />
        <Line label="Date" value={agreement && e.signatory ? date : ''} />
      </div>
    </div>
  );
};

const AdvanceRecord = ({ rows }) => (
  <div className="mt-6 break-inside-avoid" data-testid="advance-balance-record">
    <h3 className="font-bold text-sm mb-2">ADVANCE AND BALANCE RECORD</h3>
    <table className="w-full text-xs border border-gray-300">
      <thead className="bg-gray-100"><tr>{['Date', 'Description', 'Principal advanced or validly incurred', 'Payment / credit', 'Principal balance'].map(h => <th key={h} className="border border-gray-300 px-2 py-1 text-left">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.reference || i} data-testid={`advance-record-row-${i}`}>
            <td className="border border-gray-300 px-2 py-1">{r.date}</td><td className="border border-gray-300 px-2 py-1">{r.description}</td>
            <td className="border border-gray-300 px-2 py-1">{usd(r.advanced_usd)}</td><td className="border border-gray-300 px-2 py-1">{usd(r.payment_usd)}</td>
            <td className="border border-gray-300 px-2 py-1 font-semibold">{usd(r.balance_usd)}</td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={5} className="border border-gray-300 px-2 py-2 text-center text-gray-500">No advances yet</td></tr>}
      </tbody>
    </table>
  </div>
);
